import { Suspense } from 'react';

import { camelCase } from 'lodash';
import PropTypes from 'prop-types';

import BarChartIcon from '@mui/icons-material/BarChart';
import OpenInNewIcon from '@mui/icons-material/OpenInNew';
import { Box, ButtonBase, IconButton } from '@mui/material';

import { useTransl } from 'shared/lib/i18n';

import AsyncTableComponent from '../Table/AsyncTableContainer';

import { CircularLoaderComponent } from '../LoadingSpinner/LoadingSpinnerComponent';
import CallEventsComponent from './CallEventsComponent';

import { formatDate, openUrlInNewTab, toSentenceCase } from '../../utils';
import { lazyImport } from '../Main/utils';
import { getExportTitle } from '../ViewExport/utils';
import { humanizeDuration } from './utils';

import { CHART_GROUPED_BY_VALUES } from '../Chart/constants';
import { TABLE_CONSTANTS, TABLE_ID } from '../Table/constants';
import { TWILIO_CALLS_LOGS_URL } from './constants';

import {
  DATE_RANGES_TYPES,
  TABLE_DATA_TYPES,
  fullTimeDateFormat24,
} from '../../staticData';
import { CopyToClipBoardButton, OpenInNewTabButton } from '../Button';
import ErrorBoundary from '../Main/ErrorBoundary';
import {
  agentFilter,
  buildingFilter,
  callStatusFilter,
  limitFilter,
  typeFilter,
} from '../Table/filters';
import RemoteDoormanCallsExport from './RemoteDoormanCallsExport';

const ChartContainer = lazyImport(() => import('../Chart'), 'Chart');

export const getCallDetailsColumns = (t) => [
  {
    Header: t('remoteDoorman.callStartTime'),
    id: 'created_at',
    headerClassName: 'sort',
    defaultSortDesc: true,
    accessor: ({ createdAt, sid }, isExport) => {
      if (isExport) return formatDate(createdAt, true, fullTimeDateFormat24);
      const url = TWILIO_CALLS_LOGS_URL + sid;
      const tooltip = t('remoteDoorman.goCallTwilio');
      return (
        <Box display="flex" alignItems="center">
          {formatDate(createdAt, true, fullTimeDateFormat24)}
          <Box mr="1rem" />
          <OpenInNewTabButton url={url} tooltip={tooltip} />
        </Box>
      );
    },
  },
  {
    Header: t('remoteDoorman.caller'),
    id: 'building',
    headerClassName: 'sort',
    accessor: ({ buildingName, accessPointName }) =>
      buildingName ? `${buildingName} / ${accessPointName}` : t('common.NA'),
  },
  {
    Header: t('title.phoneNumber'),
    id: 'phone_number',
    sortable: false,
    accessor: ({ fromNumber }, isExport) => {
      if (!fromNumber) return t('common.NA');
      if (isExport) return fromNumber;
      return (
        <Box display="flex" alignItems="center">
          <Box>{fromNumber}</Box>
          <Box mr="1rem" />
          <CopyToClipBoardButton text={fromNumber} />
        </Box>
      );
    },
  },
  {
    Header: t('title.type'),
    id: 'type',
    sortable: false,
    accessor: ({ type }) => toSentenceCase(type),
  },
  {
    Header: t('remoteDoorman.agent'),
    id: 'agent',
    sortable: false,
    accessor: ({ forwardedOut, callEvents }) => {
      if (forwardedOut) return t('common.external');
      const reservationAcceptedEvent = callEvents?.find(
        ({ name }) => name === 'reservation_accepted',
      );
      const { name: agentName } =
        reservationAcceptedEvent?.callCenterAgent || {};
      // TODO: add link; currently no api support
      // if (isExport) return agentName;
      return agentName || t('common.NA');
    },
  },

  {
    Header: t('remoteDoorman.callStatus'),
    id: 'call_status',
    sortable: false,
    accessor: ({ status }) => toSentenceCase(status) || t('common.NA'),
  },
  {
    Header: t('remoteDoorman.callAction'),
    id: 'call_action',
    sortable: false,
    accessor: ({ actions }) =>
      toSentenceCase(actions?.join(', ')) || t('common.NA'),
  },
  {
    Header: t('remoteDoorman.callTime'),
    id: 'property',
    sortable: false,
    accessor: ({ forwardedOut, callEvents }) => {
      if (forwardedOut) return t('common.NA');
      const callEndedEvent = callEvents?.find(
        ({ name }) => name === 'call_ended',
      );
      const callStartedEvent = callEvents?.find(
        ({ name }) => name === 'reservation_accepted',
      );
      if (!callEndedEvent?.timestamp || !callStartedEvent?.timestamp)
        return t('common.NA');

      return humanizeDuration(
        formatDate(callEndedEvent?.timestamp, true, fullTimeDateFormat24),
        formatDate(callStartedEvent?.timestamp, true, fullTimeDateFormat24),
      );
    },
  },
  {
    Header: t('remoteDoorman.pickUpTime'),
    id: 'pickup_time',
    sortable: false,
    accessor: ({ forwardedOut, callEvents }) => {
      if (forwardedOut) return t('common.NA');
      const callEndedEvent = callEvents?.find(
        ({ name }) => name === 'reservation_created',
      );
      const callStartedEvent = callEvents?.find(
        ({ name }) => name === 'task_queue_entered',
      );
      if (!callEndedEvent?.timestamp || !callStartedEvent?.timestamp)
        return t('common.NA');

      return humanizeDuration(
        formatDate(callEndedEvent?.timestamp, true, fullTimeDateFormat24),
        formatDate(callStartedEvent?.timestamp, true, fullTimeDateFormat24),
      );
    },
  },
];

const RemoteDoormanCallsListComponent = (props) => {
  const { t } = useTransl();
  const {
    onLoad,
    items,
    getItems,
    listParams,
    listKey,
    onExport,
    agentOptions,
    buildings,
    isChartOpen,
    setIsChartOpen,
    onGetChartData,
  } = props;

  const columns = getCallDetailsColumns(t);

  const listParamsFormatter = (newParams) => {
    if (newParams?.cc_agent === 'external') {
      return { ...newParams, forwarded_out: true };
    }
    return { ...newParams, forwarded_out: null };
  };

  const REMOTE_DOORMAN_TYPE_OPTIONS = [
    {
      value: 'call_center',
      label: t('title.callCenter'),
    },
    {
      value: 'leasing_office',
      label: t('leasingInquiries.leasingOffice'),
    },
  ];

  const filters = isChartOpen
    ? []
    : [
        limitFilter(listParams),
        typeFilter(listParams, REMOTE_DOORMAN_TYPE_OPTIONS),
        agentFilter(listParams, agentOptions, listParamsFormatter),
        buildingFilter(listParams, buildings),
        callStatusFilter(listParams),
      ];

  const defaultParams = {
    offset: '0',
    q: null,
    created_at_after: null,
    created_at_before: null,
  };

  const CustomInfoComponent = (
    <Box display="flex" alignItems="center" mr="10rem">
      <IconButton
        onClick={() => setIsChartOpen(!isChartOpen)}
        aria-label={t('chart.chart')}
        size="small"
        sx={{
          padding: 0,
        }}
      >
        <BarChartIcon
          fontSize="small"
          color={isChartOpen ? 'primary' : 'inherit'}
        />
      </IconButton>
      <Box mx="1rem" height="1rem" borderLeft=".1rem solid #ccc" />
      <ButtonBase
        onClick={() => openUrlInNewTab(TWILIO_CALLS_LOGS_URL)}
        sx={{
          padding: 0,
          fontSize: '1.6rem',
          borderRadius: '1.5rem',
        }}
      >
        <OpenInNewIcon fontSize="inherit" />
        &nbsp;{t('remoteDoorman.seeInTwilio')}
      </ButtonBase>
    </Box>
  );

  const fieldsForBackendSort = [TABLE_CONSTANTS.created_at];
  const tableDataType = TABLE_DATA_TYPES.callRecords;

  const title = t('remoteDoorman.calls');
  const exportTitle = getExportTitle(title);
  const exportActions = (
    <RemoteDoormanCallsExport
      title={exportTitle}
      getDataForExport={onExport}
      columns={columns}
    />
  );

  const chartMetadata = {
    chartId: camelCase(title),
    title,
    label: t('frontDesk.call'),
    onGetChartData,
    isWithFilters: true,
    defaultGroupedBy: CHART_GROUPED_BY_VALUES.hourly,
  };

  return (
    <>
      {isChartOpen ? (
        <Suspense fallback={<CircularLoaderComponent />}>
          <ErrorBoundary>
            <ChartContainer chartMetadata={chartMetadata} />
          </ErrorBoundary>
        </Suspense>
      ) : null}
      <AsyncTableComponent
        tableId={TABLE_ID.remoteDoormanCalls}
        title={title}
        columns={columns}
        defaultSort={TABLE_CONSTANTS.created_at}
        fieldsForBackendSort={fieldsForBackendSort}
        filters={filters}
        showFiltersAreApplied={false}
        onLoad={onLoad}
        items={items}
        getItems={getItems}
        listParams={listParams}
        defaultParams={defaultParams}
        tableDataType={tableDataType}
        listKey={listKey}
        dateRangeType={DATE_RANGES_TYPES.created_at}
        datePicker
        manual
        defaultSortDesc
        exportActions={exportActions}
        SubComponent={(row) => <CallEventsComponent row={row} t={t} />}
        CustomInfoComponent={CustomInfoComponent}
        isHideSearch
      />
    </>
  );
};

RemoteDoormanCallsListComponent.propTypes = {
  onLoad: PropTypes.func.isRequired,
  getItems: PropTypes.func.isRequired,
  items: PropTypes.instanceOf(Object).isRequired,
  listParams: PropTypes.instanceOf(Object).isRequired,
  buildings: PropTypes.instanceOf(Object).isRequired,
  agentOptions: PropTypes.instanceOf(Array),
  listKey: PropTypes.string.isRequired,
  onExport: PropTypes.func.isRequired,
  isChartOpen: PropTypes.bool,
  setIsChartOpen: PropTypes.func.isRequired,
  onGetChartData: PropTypes.func.isRequired,
};

export default RemoteDoormanCallsListComponent;
